"use client";

import type React from "react";

import { getLocationSearchPlaceholder } from "@homeinstead/locale";
import { cn } from "@homeinstead/ui";
import { Input } from "@homeinstead/ui/components/Input";
import { MapPin } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { useLocale } from "~www/components/LocaleProvider";
import { getGoogleMapsApiKey } from "~www/lib/google-maps/api-key";

export interface PlacePredictionsMeta {
  count: number;
  loading: boolean;
}

export interface GoogleLocationAutoCompleteProps {
  value?: string;
  disabled?: boolean;
  placeholder?: string;
  fixedDropdown?: boolean;
  onInputChange: (place: string) => void;
  selectPlaceResult: (placeResult: string) => void;
  /** Called when the number of place predictions or loading state changes. */
  onPredictionsMetaChange?: (meta: PlacePredictionsMeta) => void;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const GoogleLocationAutoComplete = ({
  value,
  disabled,
  placeholder,
  fixedDropdown,
  onInputChange,
  selectPlaceResult,
  onPredictionsMetaChange,
}: GoogleLocationAutoCompleteProps) => {
  const locale = useLocale();
  const countryCode = locale.metadata.country.iso3166Alpha2Lower;
  const apiKey = getGoogleMapsApiKey();
  const { placePredictions, isPlacePredictionsLoading, getPlacePredictions } =
    usePlacesService({
      apiKey: apiKey,
      options: {
        componentRestrictions: { country: countryCode },
        input: "",
        types: ["postal_code", "country", "locality"],
      },
    });

  useEffect(() => {
    onPredictionsMetaChange?.({
      count: placePredictions.length,
      loading: isPlacePredictionsLoading,
    });
  }, [
    placePredictions.length,
    isPlacePredictionsLoading,
    onPredictionsMetaChange,
  ]);

  const [selectedPlace, setSelectedPlace] = useState<string | null>(null);
  const fixedDropdownStyles = fixedDropdown
    ? "relative"
    : "absolute left-0 top-[60px]";

  const handleSelectPlaceResult = (placeResult: string) => {
    setSelectedPlace(placeResult);
    selectPlaceResult(placeResult);
  };

  const handleInputChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    setSelectedPlace(null);
    onInputChange(value);
    getPlacePredictions({ input: value });
  };

  return (
    <div className="relative flex flex-1 flex-col items-stretch gap-2">
      <Input
        autoFocus={true}
        className="h-[48px] w-full rounded-sm border-2 border-new-beginnings-base p-4 text-new-beginnings-base"
        disabled={disabled}
        onChange={handleInputChange}
        placeholder={
          placeholder ?? getLocationSearchPlaceholder(locale.metadata.code)
        }
        value={value}
      />
      <AnimatePresence>
        {placePredictions.length > 0 && !selectedPlace && (
          <motion.ul
            animate="visible"
            className={cn(
              fixedDropdownStyles,
              "z-50 max-h-64 w-full overflow-y-auto rounded-md border border-new-beginnings-base bg-white shadow-lg",
            )}
            exit="hidden"
            initial="hidden"
            variants={containerVariants}
          >
            {placePredictions.map((item) => (
              <motion.li
                animate="visible"
                className="cursor-pointer px-4 py-3 text-new-beginnings-base/90 hover:bg-new-beginnings-base/10"
                exit="exit"
                initial="hidden"
                key={item.description}
                onClick={() => handleSelectPlaceResult(item.description)}
                variants={itemVariants}
              >
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 flex-shrink-0 text-new-beginnings-base" />
                  <span>{item.description}</span>
                </div>
              </motion.li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GoogleLocationAutoComplete;
